// `GET /extension/view-model` — the bootstrap object the JSP inlines as `window.careero`
// (plan §1.5). Typed here rather than in apps/spa because the extension popup needs the
// same call in Phase 6, and the duplicate API client is one of the things this repo exists
// to delete (§2.1).
//
// The DTO is large and only partly consumed: §1.5 measured which fields the SPA actually
// reads. Those are typed; the rest is preserved as an index signature rather than invented,
// because a field this file gets wrong is a field the port silently drops.

/** What the SPA measurably consumes today, from §1.5's usage counts. */
/** `IExtensionRegionDetailsDTO` (api/extension/extension.view.model.api.ng1.ts:3). */
export interface ExtensionRegionDetails {
  id: number
  name: string
  country: string
  displayText: string
  synonymousRegion?: string | null
  /** ⚠️ NULL ON THE WIRE for a region with no tooltip — see `LocationSection`. */
  tooltipHtml?: string | null
  showOnMetroList?: boolean
}

export interface ExtensionViewModel {
  /** Null when signed out — the endpoint answers unauthenticated. */
  session: unknown | null
  /** The LEGACY extension's hardcoded id (Constants.java). See §8.1 before using it. */
  extensionId: string
  /**
   * The ATS type's display name — an IDENTITY. Every logo, copy table and per-ATS branch keys on
   * it; never display it where `atsLabel` is meant (`getAtsLabel` in `packages/ui`).
   */
  atsName?: string | null
  /**
   * The customer-facing ATS name, for display only (`ExtensionDTO.atsLabel`). Equals `atsName`
   * unless the tenant has `EXTENSION_OVERRIDE_ATS_LABEL` on, when it is the tenant's configured
   * `extensionAtsLabel` or "ATS". Absent from a server older than careero 5cfd3ef899 — which is
   * why it is read through `getAtsLabel`, never directly.
   */
  atsLabel?: string | null
  atsType?: string | null
  featureFlags?: Record<string, boolean>
  /**
   * The user's extension groups, keyed by name — `CANDIDATES`, `CLIENT_CONTACT`, `ADMIN`.
   *
   * ⚠️ AN OBJECT, NOT AN ARRAY, and it was typed as `unknown[]` here until something read it.
   * Upstream indexes it — `careero.groups[ExtensionUserGroup.Candidates]` — and the live
   * tenant answers `{}` signed out, so the array type would have compiled, produced no error,
   * and made every membership check read `undefined`. The same shape of defect as the four
   * this package's header records: written from the endpoint's path rather than its answer.
   */
  groups?: Record<string, boolean>
  internalUser?: boolean
  alertsEnabled?: boolean
  assignToJobEnabled?: boolean
  addToJobEnabled?: boolean
  hunterAutomationEnabled?: boolean
  createClientContactInAtsEnabled?: boolean
  updateClientContactInAtsEnabled?: boolean
  atsHasJobCode?: boolean
  atsHasConcreteEmployers?: boolean
  relevantDaysForEnriched?: number
  forceEmailServerType?: unknown
  filterRawData?: unknown
  exportCandidatesEnabled?: boolean
  /** A SECOND export path, ORed with the one above behind the same feature flag. */
  exportCandidatesToExistingListEnabled?: boolean
  recaptchaSiteKey?: string
  captchaValidationDisabled?: boolean
  /**
   * `IExtensionRegionDetailsDTO[]` per country — the Location section's region checkboxes.
   * `showOnMetroList` splits them into the section's two columns.
   */
  extensionRegionDetailsByCountry?: Record<string, ExtensionRegionDetails[]>

  // ── Added by the careero-side work (plan §4.1), verified against a running dev tenant ──
  //
  // ⚠️ CASING IS THE JSP'S, NOT CAMEL CASE. The implementation resolved §1.5's key collision by
  // keeping the names the JSP already inlined — `ENV`, not `env`; `LogUrl`, not `logUrl` — so
  // the legacy extension keeps working when `Object.assign({}, window.careero, vm)` merges the
  // DTO over the rendered global. Reading `viewModel.env` returns undefined; the first port of
  // the root loader did exactly that, and the env body class silently never applied.

  /** Upstream's `careero.ENV`. Members are SCREAMING_CASE, matching the JSP. */
  ENV?: {
    IS_PROD: boolean
    IS_DEV: boolean
    IS_TEST: boolean
    IS_WHITELABEL: boolean
    IS_AGENCY: boolean
    IS_ENTERPRISE: boolean
    /** `marketplace` | `whitelabel` | … — drives `body class="{envMode}"` (§1.2). */
    MODE: string
    BRAND: string
    WEBSITE_MAIN_URL: string
  }
  /** `{ id, name, enum }` per relevant field type. */
  extensionFieldTypes?: Array<{ id: number; name: string; enum: string }>
  /** Field enum name → the ATS paths that map to it. */
  extensionPathPerField?: Record<string, Array<{ display: string; value: string }>>
  otherFieldsFieldName?: string
  /** Protocol-relative, e.g. `//<branch>.devcdn.woo.io/`. Backs `cdn-src` in 23 templates. */
  cdnUrl?: string
  baseAppUrl?: string
  /** Note the capital L, again matching the JSP. Consumed by the log factory. */
  LogUrl?: string
  badgeTypes?: unknown
  expectationTypes?: unknown
  currencies?: unknown
  degrees?: unknown
  companyTypes?: unknown
  countries?: unknown
  USAPermitType?: unknown
  EUPermitType?: unknown

  /**
   * Everything else the endpoint returns. Note what must NOT appear here: the 11 keys §1.5
   * calls Tier 3 (`countryDefaults`, `visaTypes`, `privateFeatures`, …) are read by shared code
   * but never set by the JSP, and the careero implementation correctly does not serve them.
   */
  [key: string]: unknown
}

import { apiUrl } from './request.ts'

export class ViewModelError extends Error {
  constructor(
    readonly status: number,
    readonly url: string,
  ) {
    super(`GET ${url} failed with ${String(status)}`)
    this.name = 'ViewModelError'
  }
}

export interface FetchOptions {
  /** Same-origin by default: the proxy puts the API on the SPA's own origin (§1.4, §3). */
  baseUrl?: string
  signal?: AbortSignal
  fetchImpl?: typeof fetch
}

/**
 * `credentials: 'include'` is deliberate and load-bearing: the session rides on the
 * `authctxt` / `hgui` cookies, which are host-only, which is the whole reason this call goes
 * through the proxy rather than to the tenant directly (§1.4).
 */
export async function fetchViewModel(options: FetchOptions = {}): Promise<ExtensionViewModel> {
  const { signal, fetchImpl = fetch } = options
  // Explicit override first, else whatever `configureApi` was told — the shell sets that to
  // the tenant, because its own origin is `chrome-extension://…` and serves no API.
  const url =
    options.baseUrl === undefined ? apiUrl('/extension/view-model') : `${options.baseUrl}/extension/view-model`
  const response = await fetchImpl(url, {
    credentials: 'include',
    headers: { accept: 'application/json' },
    ...(signal !== undefined ? { signal } : {}),
  })
  if (!response.ok) throw new ViewModelError(response.status, url)
  return (await response.json()) as ExtensionViewModel
}
